import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Copy,
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
  Calendar,
  Banknote,
  Building2,
  CheckSquare,
  Square,
  MinusSquare,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useLocale } from '../../../shared/hooks/useLocale';
import type { DuplicateGroup, DuplicateTransaction } from '../../transactions/api/useDuplicates';

interface DuplicateActionCardProps {
  group: DuplicateGroup;
  groupIndex: number;
  onDeleteTransaction: (txId: string) => void;
  onKeepAll: () => void;
  deletingIds: Set<string>;
  deletedIds: Set<string>;
  selectedIds?: Set<string>;
  onToggleSelect?: (txId: string) => void;
  onToggleSelectAll?: () => void;
}

function scoreColor(score: number): string {
  if (score >= 0.9) return '#dc2626';
  if (score >= 0.7) return '#d97706';
  return '#ca8a04';
}

function scoreBgClass(score: number): string {
  if (score >= 0.9)
    return 'border-red-200 bg-gradient-to-r from-red-50 to-orange-50';
  if (score >= 0.7)
    return 'border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50';
  return 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50';
}

function scoreLabel(score: number): string {
  if (score >= 0.9) return 'Very likely duplicate';
  if (score >= 0.7) return 'Likely duplicate';
  return 'Possible duplicate';
}

function typeBadge(type: string) {
  const colors: Record<string, { bg: string; text: string }> = {
    EXPENSE: { bg: '#fee2e2', text: '#b91c1c' },
    INCOME: { bg: '#dcfce7', text: '#15803d' },
    TRANSFER: { bg: '#dbeafe', text: '#1d4ed8' },
  };
  const c = colors[type] || { bg: '#f3f4f6', text: '#374151' };
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {type}
    </span>
  );
}

function CompactTransactionRow({
  tx,
  index,
  isFirst,
  onDelete,
  deleting,
  deleted,
  formatCurrency,
  isSelected,
  onToggleSelect,
}: {
  tx: DuplicateTransaction;
  index: number;
  isFirst: boolean;
  onDelete: () => void;
  deleting: boolean;
  deleted: boolean;
  formatCurrency: (amount: number, currency?: string) => string;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  if (deleted) return null;

  return (
    <div className="flex items-center gap-2 rounded-md bg-white/70 px-3 py-2">
      {/* Checkbox */}
      {onToggleSelect && (
        <button
          type="button"
          onClick={onToggleSelect}
          className="shrink-0 text-gray-400 hover:text-blue-600"
        >
          {isSelected ? (
            <CheckSquare className="h-4 w-4 text-blue-600" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </button>
      )}

      <div
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
        style={{
          backgroundColor: isFirst ? '#dcfce7' : '#f3f4f6',
          color: isFirst ? '#166534' : '#6b7280',
        }}
      >
        {index + 1}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-gray-900">
            {tx.payee || tx.description}
          </span>
          {typeBadge(tx.type)}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-0.5">
          <span className="flex items-center gap-0.5">
            <Calendar className="h-2.5 w-2.5" />
            {format(new Date(tx.date), 'MMM d')}
          </span>
          <span className="flex items-center gap-0.5">
            <Banknote className="h-2.5 w-2.5" />
            {formatCurrency(Math.abs(tx.amount), tx.currency)}
          </span>
          {tx.accountName && (
            <span className="flex items-center gap-0.5">
              <Building2 className="h-2.5 w-2.5" />
              {tx.accountName}
            </span>
          )}
        </div>
      </div>

      <div className="shrink-0">
        {isFirst ? (
          <span
            className="rounded px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: '#dcfce7', color: '#166534' }}
          >
            Keep
          </span>
        ) : (
          <button
            onClick={onDelete}
            disabled={deleting}
            className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
            {deleting ? '...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}

export function DuplicateActionCard({
  group,
  // groupIndex is used by the parent for keying/tracking; not needed internally
  groupIndex: _groupIndex,
  onDeleteTransaction,
  onKeepAll,
  deletingIds,
  deletedIds,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: DuplicateActionCardProps) {
  const { formatCurrency } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const txs = group.transactions ?? [];
  const visibleTxs = txs.filter((tx) => !deletedIds.has(tx.id));
  const visibleCount = visibleTxs.length;

  const allSelected = useMemo(() => {
    if (!selectedIds || visibleTxs.length === 0) return false;
    return visibleTxs.every((tx) => selectedIds.has(tx.id));
  }, [selectedIds, visibleTxs]);

  const someSelected = useMemo(() => {
    if (!selectedIds || allSelected) return false;
    return visibleTxs.some((tx) => selectedIds.has(tx.id));
  }, [selectedIds, allSelected, visibleTxs]);

  return (
    <div className={`rounded-lg border overflow-hidden ${scoreBgClass(group.score)}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Select all checkbox */}
            {onToggleSelectAll && (
              <button
                type="button"
                onClick={onToggleSelectAll}
                className="shrink-0 text-gray-400 hover:text-blue-600"
              >
                {allSelected ? (
                  <CheckSquare className="h-5 w-5 text-blue-600" />
                ) : someSelected ? (
                  <MinusSquare className="h-5 w-5 text-blue-400" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
              </button>
            )}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Copy className="h-4 w-4 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-gray-900 text-sm truncate block">
                {group.description}
              </span>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                <span className="flex items-center gap-1">
                  <Banknote className="h-3 w-3" />
                  {formatCurrency(group.amount, txs[0]?.currency)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(group.dateRange.from), 'MMM d')}
                  {group.dateRange.from !== group.dateRange.to &&
                    ` - ${format(new Date(group.dateRange.to), 'MMM d')}`}
                </span>
                <span>{visibleCount} transactions</span>
              </div>
            </div>
          </div>

          {/* Confidence score */}
          <div className="text-right shrink-0">
            <span
              className="text-lg font-bold"
              style={{ color: scoreColor(group.score) }}
            >
              {Math.round(group.score * 100)}%
            </span>
            <p
              className="text-[10px] font-medium"
              style={{ color: scoreColor(group.score) }}
            >
              {scoreLabel(group.score)}
            </p>
          </div>
        </div>

        {/* Reason */}
        <p className="text-xs italic text-gray-500 mb-3">{group.reason}</p>

        {/* Expand to see transactions */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 mb-2"
        >
          {expanded ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          {expanded ? 'Hide' : 'Review'} transactions ({visibleCount})
        </button>

        {/* Transaction list (expandable) */}
        {expanded && (
          <div className="space-y-1.5 mb-3">
            {txs.map((tx, txIndex) => (
              <CompactTransactionRow
                key={tx.id}
                tx={tx}
                index={txIndex}
                isFirst={txIndex === 0}
                onDelete={() => onDeleteTransaction(tx.id)}
                deleting={deletingIds.has(tx.id)}
                deleted={deletedIds.has(tx.id)}
                formatCurrency={formatCurrency}
                isSelected={selectedIds?.has(tx.id)}
                onToggleSelect={
                  onToggleSelect ? () => onToggleSelect(tx.id) : undefined
                }
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExpanded(true)}
            className="flex-1"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Review & Delete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onKeepAll}
            className="flex-1"
          >
            <Check className="h-3.5 w-3.5 mr-1" />
            Keep All
          </Button>
        </div>
      </div>
    </div>
  );
}
